import { useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { StatePaidLeaveProgram } from "@/api/types";

export type StatePaidLeaveProgramsStatus = "loading" | "ready" | "error";

export interface UseStatePaidLeaveProgramsResult {
  /**
   * The states this build can compute a paid-leave return for, or `null` until it resolves
   * and on failure.
   *
   * ⚠️ `null` and `[]` are different answers and must stay so. `[]` means the server has no
   * published rates for this year — a real finding a screen should say out loud. `null`
   * means we do not know, and a screen that collapsed the two would tell a Minnesota farmer
   * they have no state return to file because a fetch timed out.
   */
  programs: StatePaidLeaveProgram[] | null;
  status: StatePaidLeaveProgramsStatus;
}

/**
 * Which state paid-leave returns exist, for a tax year.
 *
 * WHY A SCREEN ASKS RATHER THAN KNOWS. The year-end returns row carried a tile reading
 * "MN Paid Leave" for every business in every state, and the page subtitle named "the MN
 * wage detail" the same way. Both were right by coincidence — every demo entity is
 * Minnesota — and neither had any way to be wrong on screen until somebody set a business to
 * North Dakota. The list of covered states belongs to the engine's catalog, which says so
 * itself: "All MN-specific numbers live here — never inline."
 *
 * Reference data, so it is fetched once per year and never polled: a published rate table
 * does not change while a farmer reads a return.
 *
 * A failure is NOT degraded to an empty list, for the reason on `programs` above.
 */
export function useStatePaidLeavePrograms(year: number): UseStatePaidLeaveProgramsResult {
  const [programs, setPrograms] = useState<StatePaidLeaveProgram[] | null>(null);
  const [status, setStatus] = useState<StatePaidLeaveProgramsStatus>("loading");

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    demoApi
      .listPaidLeavePrograms(year, controller.signal)
      .then((body) => {
        if (cancelled) return;
        setPrograms(body.programs);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setPrograms(null);
        setStatus("error");
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [year]);

  return { programs, status };
}
